import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap, map } from 'rxjs/operators';
import { ApiConfiguration } from './stingray-api/api-configuration';

@Injectable({
  providedIn: 'root'
})
export class BlobDownloadService {

  constructor(private http: HttpClient, private config: ApiConfiguration) { }

  download(path: string, params?: { [key: string]: string | string[] }, suggestedFilename?: string): Observable<void> {
    return this.http.get(this.config.rootUrl + path, { responseType: 'blob', observe: 'response', params: this.buildParams(params) }).pipe(
      tap((response: HttpResponse<Blob>) => {
        if (!response.body) return;
        const filename = suggestedFilename ?? this.extractFilename(response) ?? undefined;
        this.triggerDownload(response.body, filename);
      }),
      map(() => undefined as void)
    );
  }

  private extractFilename(response: HttpResponse<Blob>): string | null {
    const disposition = response.headers.get('Content-Disposition');
    if (!disposition) return null;
    const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^;"'\n]+)["']?/i);
    return match ? decodeURIComponent(match[1].trim()) : null;
  }

  open(path: string, originalFileName?: string): Observable<void> {
    return this.http.get(this.config.rootUrl + path, { responseType: 'blob' }).pipe(
      tap(blob => {
        const typedBlob = this.ensureMimeType(blob, originalFileName);
        this.triggerDownload(typedBlob, originalFileName);
      }),
      map(() => undefined as void)
    );
  }

  private ensureMimeType(blob: Blob, filename?: string): Blob {
    if (blob.type && blob.type !== 'application/octet-stream') {
      return blob;
    }
    const ext = filename?.split('.').pop()?.toLowerCase();
    const mimeTypes: Record<string, string> = {
      pdf: 'application/pdf',
      jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      doc: 'application/msword',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      xls: 'application/vnd.ms-excel',
      pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      ppt: 'application/vnd.ms-powerpoint',
      txt: 'text/plain', csv: 'text/csv', rtf: 'application/rtf',
    };
    const type = ext && mimeTypes[ext];
    return type ? new Blob([blob], { type }) : blob;
  }

  downloadAsBlob(path: string, params?: { [key: string]: string | string[] }): Observable<Blob> {
    return this.http.get(this.config.rootUrl + path, { responseType: 'blob', params: this.buildParams(params) });
  }

  private buildParams(params?: { [key: string]: string | string[] }): HttpParams {
    let httpParams = new HttpParams();
    if (params) {
      for (const key of Object.keys(params)) {
        const val = params[key];
        if (Array.isArray(val)) {
          for (const v of val) {
            httpParams = httpParams.append(key, v);
          }
        } else if (val !== null && val !== undefined) {
          httpParams = httpParams.set(key, val);
        }
      }
    }
    return httpParams;
  }

  private triggerDownload(blob: Blob, filename?: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    if (filename) {
      a.download = filename;
    }
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}
